<template>
  <div class="pa-4">
    <breadcrumb />

    <div class="flex-wrap mb-4 d-flex align-center justify-space-between">
      <div class="d-flex align-center">
        <h2 class="mr-3 black--text">{{ $t("reports.cropProgression.title") }}</h2>
        <v-btn small rounded color="primary" depressed @click="showGuide = true">
          <v-icon left small>mdi-help-circle-outline</v-icon>
          {{ $t("reports.cropProgression.aiReportGuide") }}
        </v-btn>
      </div>
      <div>
        <v-btn outlined color="primary" class="mr-2" @click="goBack">{{ $t("reports.cropProgression.back") }}</v-btn>
        <v-btn outlined color="red" class="mr-2" @click="deleteDialog = true">{{ $t("reports.cropProgression.deleteReport") }}</v-btn>
        <v-btn color="primary" depressed :disabled="!isDownloadable" :loading="downloading" @click="download">
          {{ $t("reports.cropProgression.downloadReport") }}
        </v-btn>
      </div>
    </div>

    <v-card class="pa-4" outlined v-if="report">
      <!-- Meta -->
      <h3 class="mb-3 black--text">{{ report.farmName ? report.farmName + " Report" : $t("reports.cropProgression.title") }}</h3>
      <v-row class="mb-1">
        <v-col cols="6" md="3">
          <div class="grey--text text-caption">{{ $t("reports.cropProgression.dateOfReport") }}</div>
          <div class="font-weight-bold">{{ report.createdAt | formatDate }}</div>
        </v-col>
        <v-col cols="6" md="3">
          <div class="grey--text text-caption">{{ $t("reports.cropProgression.farm") }}</div>
          <div class="font-weight-bold">{{ report.farmName || "-" }}</div>
        </v-col>
        <v-col cols="6" md="3">
          <div class="grey--text text-caption">{{ $t("reports.cropProgression.zone") }}</div>
          <div class="font-weight-bold">{{ report.zoneName || "-" }}</div>
        </v-col>
        <v-col cols="6" md="3">
          <div class="grey--text text-caption">{{ $t("reports.cropProgression.location") }}</div>
          <div class="font-weight-bold">{{ report.locationName || "-" }}</div>
        </v-col>
      </v-row>

      <v-btn color="primary" depressed class="my-3" @click="goCompare">
        {{ $t("reports.cropProgression.compareImages") }}
      </v-btn>

      <!-- Map + status overlay -->
      <div class="map-holder">
        <div v-if="isGenerating" class="gen-overlay">
          <v-progress-circular indeterminate color="primary" size="42" />
          <div class="mt-3 black--text">{{ $t("reports.cropProgression.generating") }}</div>
          <div class="grey--text text-caption">{{ $t("reports.cropProgression.generatingHelp") }}</div>
        </div>
        <v-btn v-if="dates.length" icon class="nav-arrow left" @click="stepDate(-1)"><v-icon>mdi-chevron-left</v-icon></v-btn>
        <v-btn v-if="dates.length" icon class="nav-arrow right" @click="stepDate(1)"><v-icon>mdi-chevron-right</v-icon></v-btn>
        <index-overlay-map
          :overlays="overlays"
          :outline-coordinates="report.coordinates || []"
          :circle="circleOutline"
          height="460px"
        />
      </div>

      <v-alert v-if="report.status === 'PARTIAL'" type="warning" dense text class="mt-2">
        {{ $t("reports.cropProgression.partialWarning") }}
      </v-alert>
      <v-alert v-if="report.status === 'FAILED'" type="error" dense text class="mt-2">
        {{ $t("reports.cropProgression.failed") }}
      </v-alert>

      <!-- Date strip -->
      <div class="mt-3 date-strip" v-if="dates.length">
        <span class="mr-3 black--text font-weight-medium">{{ $t("reports.cropProgression.selectDate") }}</span>
        <v-slide-group
          ref="dateSlide"
          v-model="activeDate"
          center-active
          show-arrows
          class="date-slide"
          @wheel.native.prevent="onDateWheel"
          @mousedown.native="onDateDragStart"
        >
          <v-slide-item v-for="d in dates" :key="d" :value="d">
            <v-btn
              text
              small
              class="mx-1"
              :class="{ 'active-date': d === report.selectedDate }"
              @click="onDateClick(d)"
            >
              {{ d | formatDate }}
            </v-btn>
          </v-slide-item>
        </v-slide-group>
      </div>

      <v-divider class="my-4" />

      <!-- Report type (index) -->
      <h4 class="mb-1 primary--text">{{ $t("reports.cropProgression.reportType") }}</h4>
      <p class="mb-2 grey--text text-caption">{{ $t("reports.cropProgression.chooseIndexHelp") }}</p>
      <v-select
        v-model="selectedIndex"
        :items="indexItems"
        :label="$t('reports.cropProgression.selectReportType')"
        outlined
        dense
        hide-details
        style="max-width: 420px"
        class="mb-4"
      />

      <index-legend :index-type="selectedIndex" :metadata="metadata" :label="indexLabel(selectedIndex)" />

      <v-alert type="warning" text class="mt-4" border="left" icon="mdi-alert">
        <strong>{{ $t("reports.cropProgression.disclaimerTitle") }}</strong><br />
        {{ $t("reports.cropProgression.disclaimer") }}
      </v-alert>

      <v-divider class="my-4" />

      <!-- Feedback -->
      <feedback-display
        :feedback="report.feedback"
        i18n-prefix="reports.cropProgression"
        @add="onAddFeedback"
      />
    </v-card>

    <feedback-dialog
      :value="showFeedback"
      :report-uid="reportUid"
      :service="feedbackService"
      :default-agree="feedbackDefaultAgree"
      i18n-prefix="reports.cropProgression"
      upload-folder="crop-progression/feedback"
      @input="showFeedback = $event"
      @submitted="onFeedbackSubmitted"
    />
    <guide-dialog :value="showGuide" @input="showGuide = $event" :metadata="metadata" />

    <confirm-box
      v-if="deleteDialog"
      :dialog="deleteDialog"
      :title="$t('reports.cropProgression.deleteConfirmTitle')"
      :message="$t('reports.cropProgression.deleteConfirmMessage')"
      :confirm-btn-text="$t('reports.cropProgression.delete')"
      :cancel-btn-text="$t('reports.cropProgression.cancel')"
      @continue:action="doDelete"
      @cancel:action="deleteDialog = false"
    />
    <confirm-box
      v-if="generatePrompt"
      :dialog="generatePrompt"
      :title="$t('reports.cropProgression.selectDate')"
      :message="$t('reports.cropProgression.notGeneratedPrompt')"
      :confirm-btn-text="$t('reports.cropProgression.generateNow')"
      :cancel-btn-text="$t('reports.cropProgression.cancel')"
      @continue:action="doGenerateForDate"
      @cancel:action="generatePrompt = false"
    />
  </div>
</template>

<script>
import CropProgressionService from "@/_services/CropProgressionService";
import IndexOverlayMap from "./components/IndexOverlayMap.vue";
import IndexLegend from "./components/IndexLegend.vue";
import FeedbackDialog from "../shared/FeedbackDialog.vue";
import FeedbackDisplay from "../shared/FeedbackDisplay.vue";
import GuideDialog from "./components/GuideDialog.vue";
import ConfirmBox from "@/components/ConfirmBox.vue";
import loadingMixin from "@/mixins/LoadingMixin";
import downloadMixin from "@/mixins/DownloadMixin";
import reportError from '../../../mixins/reportError.js';

const INDICES = ["NDVI", "MSAVI", "LSWI", "RECI", "NDRE"];
const POLL_MS = 4000;

export default {
  name: "CropProgressionView",
  components: { IndexOverlayMap, IndexLegend, FeedbackDialog, FeedbackDisplay, GuideDialog, ConfirmBox },
  mixins: [loadingMixin, downloadMixin, reportError],
  data() {
    return {
      report: null,
      metadata: {},
      dates: [],
      generatedMap: {},
      selectedIndex: "NDVI",
      showFeedback: false,
      feedbackDefaultAgree: null,
      showGuide: false,
      deleteDialog: false,
      generatePrompt: false,
      pendingDate: null,
      downloading: false,
      pollTimer: null,
      dragStartX: 0,
      dragStartOffset: 0,
      dragMoved: false,
    };
  },
  computed: {
    feedbackService() {
      return CropProgressionService;
    },
    reportUid() {
      return this.$route.params.reportUid;
    },
    isGenerating() {
      return this.report && this.report.status === "GENERATING";
    },
    isDownloadable() {
      return this.report && ["COMPLETED", "PARTIAL"].includes(this.report.status);
    },
    currentIndex() {
      if (!this.report || !this.report.indices) return null;
      return this.report.indices.find((i) => i.indexType === this.selectedIndex) || null;
    },
    overlays() {
      const idx = this.currentIndex;
      if (idx && idx.url && this.report.bbox) {
        return [{ url: idx.url, bbox: this.report.bbox, opacity: 1, clipRightPct: null }];
      }
      return [];
    },
    circleOutline() {
      if (this.report && this.report.radius) {
        return {
          lat: this.report.centerLatitude,
          lng: this.report.centerLongitude,
          radiusKm: this.report.radius,
        };
      }
      return null;
    },
    indexItems() {
      return INDICES.map((i) => ({ text: this.indexLabel(i), value: i }));
    },
    // Drives the slide-group's active item so the selected date scrolls into view
    // on load (center-active). Navigation happens via the button click, so the
    // setter is a no-op.
    activeDate: {
      get() {
        return this.report ? this.report.selectedDate : null;
      },
      set() {},
    },
  },
  watch: {
    "$route.params.reportUid"() {
      this.reload();
    },
  },
  created() {
    this.loadMetadata();
    this.reload();
  },
  beforeDestroy() {
    this.clearPoll();
    window.removeEventListener("mousemove", this.onDateDrag);
    window.removeEventListener("mouseup", this.onDateDragEnd);
  },
  methods: {
    indexLabel(i) {
      return this.$t(`reports.cropProgression.index${i}`);
    },
    clearPoll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async reload() {
      this.clearPoll();
      this.startLoading();
      try {
        await this.fetchReport();
        await this.fetchRelatedDates();
        if (this.isGenerating) this.startPolling();
      } finally {
        this.stopLoading();
      }
    },
    async fetchReport() {
      try {
        const env = await CropProgressionService.getReport(this.reportUid);
        this.report = (env && env.data) || null;
      } catch (e) {
        this.$notify({ text: this.$t("reports.cropProgression.loadFailed"), type: "error" });
      }
    },
    startPolling() {
      this.clearPoll();
      this.pollTimer = setInterval(async () => {
        await this.fetchReport();
        if (!this.isGenerating) this.clearPoll();
      }, POLL_MS);
    },
    async fetchRelatedDates() {
      try {
        const env = await CropProgressionService.getRelatedDates(this.reportUid);
        const d = (env && env.data) || {};
        this.dates = d.dates || [];
        this.generatedMap = d.generated || {};
      } catch (e) {
        this.dates = [];
      }
    },
    async loadMetadata() {
      try {
        const env = await CropProgressionService.getIndicesMetadata();
        this.metadata = (env && env.data) || {};
      } catch (e) {
        this.metadata = {};
      }
    },
    // Mouse wheel + drag scrolling for the date strip. Drives the slide-group's
    // own `scrollOffset` (the same state its arrows use), so all three stay in sync.
    dateSlideEls() {
      const sg = this.$refs.dateSlide;
      if (!sg || !sg.$el) return null;
      return {
        sg,
        wrapper: sg.$el.querySelector(".v-slide-group__wrapper"),
        content: sg.$el.querySelector(".v-slide-group__content"),
      };
    },
    scrollDateBy(delta) {
      const els = this.dateSlideEls();
      if (!els || !els.wrapper || !els.content) return;
      const max = Math.max(0, els.content.scrollWidth - els.wrapper.clientWidth);
      const cur = Number(els.sg.scrollOffset) || 0;
      els.sg.scrollOffset = Math.min(Math.max(cur + delta, 0), max);
    },
    onDateWheel(e) {
      this.scrollDateBy(e.deltaY !== 0 ? e.deltaY : e.deltaX);
    },
    onDateDragStart(e) {
      const els = this.dateSlideEls();
      if (!els) return;
      this.dragStartX = e.clientX;
      this.dragStartOffset = Number(els.sg.scrollOffset) || 0;
      this.dragMoved = false;
      window.addEventListener("mousemove", this.onDateDrag);
      window.addEventListener("mouseup", this.onDateDragEnd);
    },
    onDateDrag(e) {
      const els = this.dateSlideEls();
      if (!els || !els.wrapper || !els.content) return;
      const dx = e.clientX - this.dragStartX;
      if (Math.abs(dx) > 3) this.dragMoved = true;
      const max = Math.max(0, els.content.scrollWidth - els.wrapper.clientWidth);
      els.sg.scrollOffset = Math.min(Math.max(this.dragStartOffset - dx, 0), max);
    },
    onDateDragEnd() {
      window.removeEventListener("mousemove", this.onDateDrag);
      window.removeEventListener("mouseup", this.onDateDragEnd);
      // Keep dragMoved set through the click that follows mouseup, then clear it.
      this.$nextTick(() => {
        this.dragMoved = false;
      });
    },
    stepDate(dir) {
      if (!this.dates.length) return;
      const i = this.dates.indexOf(this.report.selectedDate);
      const next = this.dates[i + dir];
      if (next) this.onDateClick(next);
    },
    onDateClick(date) {
      // Ignore the click that ends a drag-scroll.
      if (this.dragMoved) return;
      if (date === this.report.selectedDate) return;
      const gen = this.generatedMap[date];
      if (gen && gen.reportUid) {
        this.$router.push({ name: "cropProgressionView", params: { reportUid: gen.reportUid } });
        return;
      }
      this.pendingDate = date;
      this.generatePrompt = true;
    },
    async doGenerateForDate() {
      this.generatePrompt = false;
      if (!this.pendingDate) return;
      this.startLoading();
      try {
        const env = await CropProgressionService.compareReports({
          reportUid: this.reportUid,
          dates: [this.pendingDate],
        });
        const reports = (env && env.data && env.data.reports) || [];
        if (reports[0] && reports[0].reportUid) {
          this.$router.push({ name: "cropProgressionView", params: { reportUid: reports[0].reportUid } });
        } else {
          this.$notify({
            text: this.reportErrorMessage({ data: env }, 'reports.cropProgression.generateFailed'),
            type: 'error',
          });
        }
      } catch (e) {
        this.$notify({
          text: this.reportErrorMessage(e, 'reports.cropProgression.generateFailed'),
          type: 'error',
        });
      } finally {
        this.pendingDate = null;
        this.stopLoading();
      }
    },
    async download() {
      this.downloading = true;
      try {
        const res = await CropProgressionService.downloadPdf(this.reportUid);
        this.downloadBlob(res.data, `vegetation_vigor_${this.reportUid}.pdf`);
      } catch (e) {
        this.$notify({ text: this.$t("reports.cropProgression.loadFailed"), type: "error" });
      } finally {
        this.downloading = false;
      }
    },
    onAddFeedback(agree) {
      this.feedbackDefaultAgree = agree;
      this.showFeedback = true;
    },
    onFeedbackSubmitted() {
      this.showFeedback = false;
      this.$notify({ title: this.$t("success"), text: this.$t("reports.cropProgression.feedbackSubmitted"), type: "success" });
      this.fetchReport();
    },
    async doDelete() {
      this.deleteDialog = false;
      this.startLoading();
      try {
        await CropProgressionService.deleteReport(this.reportUid);
        this.$notify({ title: this.$t("success"), text: this.$t("reports.cropProgression.deleted"), type: "success" });
        this.$router.push({ name: "cropProgressionList" });
      } catch (e) {
        this.$notify({ text: this.$t("reports.cropProgression.loadFailed"), type: "error" });
      } finally {
        this.stopLoading();
      }
    },
    goCompare() {
      this.$router.push({ name: "cropProgressionCompare", params: { reportUid: this.reportUid } });
    },
    goBack() {
      this.$router.push({ name: "cropProgressionList" });
    },
  },
};
</script>

<style scoped>
.map-holder {
  position: relative;
}
.gen-overlay {
  position: absolute;
  z-index: 5;
  inset: 0;
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
}
.nav-arrow {
  position: absolute;
  z-index: 4;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.9);
}
.nav-arrow.left {
  left: 8px;
}
.nav-arrow.right {
  right: 8px;
}
.date-strip {
  display: flex;
  align-items: center;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 6px 10px;
  overflow: hidden;
}
/* Let the slide-group take the remaining width and scroll internally instead of
   overflowing the card (a flex child defaults to min-width:auto). */
.date-strip ::v-deep .v-slide-group {
  min-width: 0;
  flex: 1 1 auto;
}
.date-strip ::v-deep .v-slide-group__content {
  flex-wrap: nowrap;
}
.date-strip ::v-deep .v-slide-group__wrapper {
  cursor: grab;
}
.date-strip ::v-deep .v-slide-group__wrapper:active {
  cursor: grabbing;
}
.active-date {
  color: #159957 !important;
  border-bottom: 2px solid #159957;
  font-weight: bold;
}
</style>
